import type { Task } from '@saniplus/contracts';
import { ChevronRight, StatusChip, decorativeIcon } from '@saniplus/ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { TASKS_PATH } from '../../../app/config';

/** Three titles, then an overflow row. The page scrolls (C-19), so the budget is legibility, not height. */
const SHOWN = 3;

export interface HomeTasksLineProps {
  /**
   * Every task of the current-or-next shift (T-14). The pending ones are
   * listed by title; the closed ones (`done`/`unable`) fold into one count
   * row, so the way to `/taches` — which is not in the bottom nav — never
   * disappears the moment the last task is closed (device report, 2026-09-07).
   */
  readonly tasks: readonly Task[];
  readonly className?: string;
}

/**
 * The home screen's open tasks — **the titles, not a count** (P3 §6, T-14).
 *
 * A worker sees this **before** punch-in — `GET /me/tasks` already resolves
 * to the caller's current-or-next shift, so the same list is correct whether
 * the worker is off duty or on the clock.
 *
 * ## Why titles, and why here (2026-09-03)
 *
 * It shipped as a scrollable « N tâches assignées » line under the instrument
 * panel. Two device reports killed both halves of that.
 *
 * 1. **It was below the fold.** On a 390x844 phone the on-duty panel plus the
 *    punch key filled the column, so the line was the first thing under it —
 *    on a screen with no scroll affordance of any kind, where an invisible
 *    line and no line at all are the same fact to the reader. It is now in the
 *    `shrink-0` thumb zone, which makes its visibility STRUCTURAL rather than
 *    a height calculation any longer French site name can break.
 * 2. **A count is not an answer.** « 1 tâche à faire » on a gated OUT key told
 *    a worker a task existed and made them tap through to `/taches` to learn
 *    WHICH — on the one screen they read in thirty seconds with a glove on.
 *    The first two titles are on the home screen now, each its own 48 px
 *    target, and only the third and beyond fold into « +N autres ».
 *
 * It does **not** stand down for `PunchOutGate`: the gate's key still says how
 * many block the punch out, and saying the number there and the titles here is
 * redundancy a worker benefits from, not noise.
 *
 * The blocking flag is `/taches`' own `StatusChip tone="danger"` under
 * `flags.blocking`, not a second vocabulary — the row a worker taps and the
 * row they land on say the same word in the same field.
 */
export function HomeTasksLine({ tasks, className }: HomeTasksLineProps) {
  const { t } = useTranslation('tasks');
  if (tasks.length === 0) return null;

  const pending = tasks.filter((task) => task.status === 'pending');
  const closed = tasks.filter((task) => task.status === 'done' || task.status === 'unable').length;
  const shown = pending.slice(0, SHOWN);
  const overflow = pending.length - shown.length;

  return (
    <section className={className}>
      {/* A visible name for the list (2026-09-03 device report): bare titled
          rows between the state panel and the punch key read as unexplained
          buttons. Same label register as `InstrumentPanel`'s `footerLabel`. */}
      <h2 className="mb-1.5 text-label uppercase text-ink-muted">{t('list.title')}</h2>
      <ul
        data-testid="home-tasks-line"
        className="overflow-hidden rounded-control border border-border-field bg-surface"
      >
      {shown.map((task) => (
        <li key={task.id} className="border-t border-hairline first:border-t-0">
          <Link
            to={TASKS_PATH}
            data-testid={`home-task-${task.id}`}
            className="flex min-h-touch items-center gap-2 px-3 py-2 hover:bg-surface-sunk"
          >
            <span className="min-w-0 flex-1 truncate text-body text-ink">{task.title}</span>
            {task.blocksPunchOut ? (
              <StatusChip tone="danger">{t('flags.blocking')}</StatusChip>
            ) : null}
            <ChevronRight
              {...decorativeIcon('sm')}
              aria-hidden="true"
              className="shrink-0 text-ink-muted"
            />
          </Link>
        </li>
      ))}

      {overflow > 0 ? (
        <li className="border-t border-hairline">
          <Link
            to={TASKS_PATH}
            data-testid="home-tasks-more"
            className="flex min-h-touch items-center justify-between gap-2 px-3 py-2 hover:bg-surface-sunk"
          >
            <span className="min-w-0 truncate text-body text-ink-muted">
              {t('home.more', { count: overflow })}
            </span>
            <ChevronRight
              {...decorativeIcon('sm')}
              aria-hidden="true"
              className="shrink-0 text-ink-muted"
            />
          </Link>
        </li>
      ) : null}

      {closed > 0 ? (
        <li className="border-t border-hairline first:border-t-0">
          <Link
            to={TASKS_PATH}
            data-testid="home-tasks-closed"
            className="flex min-h-touch items-center justify-between gap-2 px-3 py-2 hover:bg-surface-sunk"
          >
            <span className="min-w-0 truncate text-body text-ink-muted">
              {t('home.closed', { count: closed })}
            </span>
            <ChevronRight
              {...decorativeIcon('sm')}
              aria-hidden="true"
              className="shrink-0 text-ink-muted"
            />
          </Link>
        </li>
      ) : null}
      </ul>
    </section>
  );
}
